import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Settings,
  ChevronDown,
  Shield,
  Clock,
  Database,
  BarChart3,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import { useUsage } from "../contexts/UsageContext";

const UserMenu = () => {
  const { user, logout } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { usageData, isLimitReached } = useUsage();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setIsOpen(false);
  };

  if (!user) {
    return null;
  }

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleLabel = (role) => {
    if (role === "admin") return t("admin.admin");
    if (role === "demo") return t("admin.demo", "Demo");
    return t("admin.user");
  };

  const isDemo = user?.role === "demo";

  // Get color based on usage percentage
  const getUsageColor = () => {
    const percentage = usageData.percentage_used;
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 75) return "text-orange-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-green-600";
  };

  const getProgressBarColor = () => {
    const percentage = usageData.percentage_used;
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-orange-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* User Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 sm:space-x-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all"
      >
        {/* User Avatar */}
        <div
          className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center ${
            isDemo
              ? "bg-gradient-to-br from-amber-500 to-orange-500"
              : "bg-gradient-to-br from-blue-500 to-purple-500"
          }`}
        >
          <span className="text-[10px] sm:text-xs font-semibold text-white">
            {getInitials(user.full_name)}
          </span>
        </div>

        {/* User Info - Only show name on larger screens */}
        <div className="hidden lg:block text-left">
          <div className="text-xs font-medium text-gray-700 truncate max-w-[100px]">
            {user.full_name}
          </div>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
          {/* User Info Header */}
          <div
            className={`px-4 py-3 border-b border-gray-100 ${
              isDemo
                ? "bg-gradient-to-r from-amber-50 to-orange-50"
                : "bg-gradient-to-r from-blue-50 to-purple-50"
            }`}
          >
            <div className="flex items-center space-x-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isDemo
                    ? "bg-gradient-to-br from-amber-500 to-orange-500"
                    : "bg-gradient-to-br from-blue-500 to-purple-500"
                }`}
              >
                <span className="text-sm font-semibold text-white">
                  {getInitials(user.full_name)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {user.full_name}
                </div>
                <div className="text-xs text-gray-600 truncate">
                  {user.email}
                </div>
                <div
                  className={`text-xs font-medium ${
                    isDemo ? "text-amber-600" : "text-blue-600"
                  }`}
                >
                  {getRoleLabel(user.role)}
                  {isDemo && " Account"}
                </div>
              </div>
            </div>
          </div>

          {/* Usage Information - Visible on mobile */}
          <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <BarChart3 className={`h-4 w-4 ${getUsageColor()}`} />
                <span className="text-sm font-medium text-gray-700">
                  {t("usage.monthlySearches", "Monthly Searches")}
                </span>
              </div>
              {isLimitReached && (
                <AlertCircle className="h-4 w-4 text-red-500" />
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">
                  {t("usage.used", "Used")}:
                </span>
                <span className={`font-medium ${getUsageColor()}`}>
                  {usageData.current_usage} / {usageData.limit} (
                  {usageData.percentage_used.toFixed(0)}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
                  style={{
                    width: `${Math.min(usageData.percentage_used, 100)}%`,
                  }}
                />
              </div>
              {isLimitReached && (
                <p className="text-xs text-red-600 mt-1">
                  {t("usage.limitReached", "Monthly limit reached")}
                </p>
              )}
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Search History */}
            <button
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => {
                setIsOpen(false);
                navigate("/search-history");
              }}
            >
              <Clock className="h-4 w-4 mr-3 text-gray-500" />
              {t("header.searchHistory")}
            </button>

            {/* Admin Dashboard - Only show for admins */}
            {user.role === "admin" && (
              <>
                <button
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/admin");
                  }}
                >
                  <Shield className="h-4 w-4 mr-3 text-gray-500" />
                  {t("header.adminDashboard")}
                </button>
                <button
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/admin/query-logs");
                  }}
                >
                  <Database className="h-4 w-4 mr-3 text-gray-500" />
                  {t("header.queryLogs")}
                </button>
                <button
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/admin/settings");
                  }}
                >
                  <Settings className="h-4 w-4 mr-3 text-gray-500" />
                  {t("header.promptSettings")}
                </button>
              </>
            )}

            {/* Divider */}
            <div className="border-t border-gray-100 my-2"></div>

            {/* Logout */}
            <button
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-3" />
              {t("header.signOut")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
